import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import { Grid, Tooltip } from "@mui/joy";
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AbsyzLogo from "../../assessts/images/absyz_logo.png";
import headerBgColor from "../../assessts/images/headercolor.png";
import LogoutIcon from "../../assessts/images/LogoutIcon.png";
import UserIcon from "../../assessts/images/userIcon.png";
import BgColor from "../../assessts/images/bgColor.png";
import PushPinIcon from '@mui/icons-material/PushPin';
import AIChatBox from "../AIBot/AIBot";
import ReusableModal from "../../components/ReusableModal/ReusableModal";
import constants from "../../utils/constants";
import "./UserDashboard.css"
import CloseIcon from '@mui/icons-material/Close';
import { Button, useMediaQuery, IconButton, TextField, Menu, MenuItem } from "@mui/material";
import { Delete, Get, Post, Put } from "../../services/apiServices";
import { networkUrls } from "../../services/networkurls";
import {
  listChatSessions,
  renameChatSession,
  pinChatSession,
  deleteChatSession,
} from "../../services/chatSessionService";
import ReuseableSnackbar from "../../components/Snackbar/Snackbar";
import { Virtuoso } from 'react-virtuoso';

const drawerWidth = 240;
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": {
          ...openedMixin(theme),
          backgroundImage: `url(${BgColor})`,
        },
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": {
          ...closedMixin(theme),
          backgroundImage: `url(${BgColor})`,
        },
      },
    },
  ],
}));

// Mobile drawer that slides from top
const MobileDrawer = styled(MuiDrawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    backgroundColor: "white",
    width: "50%",
    height: "100vh",
  
    top: "10px", 
    borderRadius: "0 0 8px 8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  },
}));

// Session Item Component for Virtualization
const SessionItem = React.memo(({ 
  session, 
  isActive, 
  isEditing, 
  editingTitle, 
  onSessionClick, 
  onTitleClick, 
  onTitleChange, 
  onSaveTitle, 
  onCancelEdit, 
  onTitleBlur, 
  onMenuOpen 
}: any) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  // iOS-specific fix: manually focus and trigger keyboard
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // iOS-specific: trigger click to ensure keyboard appears
          inputRef.current.click();
        }
      }, 100);
    }
  }, [isEditing]);

  return (
    <Box
      sx={{
        padding: "5px 12px",
        margin: "4px 8px",
        borderRadius: "25px",
        backgroundColor: isActive ? "#52a4ec" : "#CCE7FF",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        "&:hover": { backgroundColor: "#61b1f6" },
        border: session.is_pinned ? "2px solid #087BDF" : "2px solid transparent",
        boxShadow: session.is_pinned ? "0 2px 4px rgba(8, 123, 223, 0.2)" : "none",
      }}
      onClick={(e) => {
        // Don't trigger session click when in editing mode
        if (!isEditing) {
          onSessionClick();
        }
      }}
    >
      {isEditing ? (
        <TextField
          value={editingTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSaveTitle();
            } else if (e.key === 'Escape') {
              onCancelEdit();
            }
          }}
          onBlur={onTitleBlur}
          inputRef={inputRef}
          autoFocus
          size="small"
          variant="standard"
          onClick={(e) => e.stopPropagation()}
          inputProps={{
            inputMode: 'text',
            enterKeyHint: 'done'
          }}
          sx={{
            minWidth: "120px",
            "& .MuiInputBase-input": {
              fontSize: "14px",
              color: "black",
              padding: "2px 4px",
            },
            "& .MuiInput-underline:before": {
              borderBottom: "1px solid #087BDF",
            },
            "& .MuiInput-underline:after": {
              borderBottom: "2px solid #087BDF",
            },
          }}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
          {session.is_pinned && (
            <PushPinIcon 
              sx={{ 
                fontSize: "16px", 
                color: "#087BDF", 
                marginRight: "6px",
                flexShrink: 0
              }} 
            />
          )}
          <Tooltip title={session.title} placement="right">
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: session.is_pinned ? "130px" : "150px",
                color: "black",
                cursor: "pointer",
              }}
              onDoubleClick={(e) => onTitleClick(session.id, session.title, e)}
            >
              {session.title}
            </span>
          </Tooltip>
        </Box>
      )}

      <IconButton
        onClick={(e) => onMenuOpen(e, session.id)}
        size="small"
        sx={{ color: "#087BDF", padding: "4px" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

export default function MiniDrawer() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const[isNewChatClicked,setIsNewChatClicked]=React.useState(false);
  const [sessions, setSessions] = React.useState<any[]>([]);
  const [activeSessionId, setActiveSessionId] = React.useState<string | null>(null);
  const [snackbarInfo, setSnackbarInfo] = React.useState({
    color: "",
    message: "",
  });
  const [isShowSnackbar, setIsShowSnackbar] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedSessionForMenu, setSelectedSessionForMenu] = React.useState<string | null>(null);
  const [sessionToDelete, setSessionToDelete] = React.useState<string | null>(null);
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(null);
  const [editingTitle, setEditingTitle] = React.useState("");


  // Debug logging for state changes
 

  const [openLogoutModal, setOpenLogoutModal] = React.useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleIconClick = () => {

    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000);

  };

  // const handleDrawerOpen = () => {
  //   setOpen(true);
  // };

  // const handleDrawerClose = () => {
  //   setOpen(false);
  // };

  const handleNewChat = () => {
    setActiveSessionId(null);
    setIsNewChatClicked(true);
    if (isMobile) setOpen(false);
  };

  const updateSessionTitle = (sessionId: string, newTitle: string) => {
    setSessions((prev) =>
      prev.map((session) =>
        session.id === sessionId ? { ...session, title: newTitle } : session
      )
    );
    renameChatSession(sessionId, newTitle);
  };

  const handleCreateNewSession = (newSession: any) => {
    setSessions((prev) => [newSession, ...prev]);
    setActiveSessionId(newSession.id);
    setIsNewChatClicked(true);
  };




  const handleDeleteSession = (sessionId: string) => {
    setDeleteModalOpen(false);
    deleteChatSession(sessionId);
    const updatedSessions = sessions.filter((s) => s.id !== sessionId);
    setSessions(updatedSessions);
    if (activeSessionId === sessionId) {
      setActiveSessionId(updatedSessions.length ? updatedSessions[0].id : null);
    }
  };

  const handleOpenLogoutModal = () => {
    setOpenLogoutModal(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, sessionId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedSessionForMenu(sessionId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedSessionForMenu(null);
  };

  const handleEditTitle = () => {
    if (selectedSessionForMenu) {
      const session = sessions.find(s => s.id === selectedSessionForMenu);
      if (session) {
        setEditingSessionId(selectedSessionForMenu);
        setEditingTitle(session.title);
      }
    }
    handleMenuClose(); // Close menu after setting edit state
  };

  const handleSaveTitle = () => {
    if (editingSessionId && editingTitle.trim()) {
      updateSessionTitle(editingSessionId, editingTitle.trim());
    }
    setEditingSessionId(null);
    setEditingTitle("");
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingTitle("");
  };



  const handleTitleClick = (sessionId: string, currentTitle: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent session selection when clicking on title
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const handleTitleBlur = () => {
    if (editingSessionId && editingTitle.trim() && editingTitle.trim() !== sessions.find(s => s.id === editingSessionId)?.title) {
      handleSaveTitle();
    } else {
      handleCancelEdit();
    }
  };

  // Handle clicks outside of editing area to cancel edit mode
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingSessionId) {
        const target = event.target as HTMLElement;
        // Check if click is outside the editing TextField
        if (!target.closest('.MuiTextField-root')) {
          handleTitleBlur();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingSessionId, editingTitle, sessions]);

  const handleDeleteClick = (sessionId: string) => {
    setSessionToDelete(sessionId);
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  // Clear sessionToDelete when delete modal closes
  React.useEffect(() => {
    if (!deleteModalOpen) {
      setSessionToDelete(null);
    }
  }, [deleteModalOpen, sessionToDelete]);

  const handlePinToggle = (sessionId: string, currentPinStatus: boolean) => {
    pinChatSession(sessionId, !currentPinStatus);
    setSessions((prev) => {
      let updated = prev.map((s) =>
        s.id === sessionId ? { ...s, is_pinned: !currentPinStatus } : s
      );
      if (!currentPinStatus) {
        const pinned = updated.find((s) => s.id === sessionId)!;
        updated = [pinned, ...updated.filter((s) => s.id !== sessionId)];
      } else {
        updated.sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return 0;
        });
      }
      return updated;
    });
    handleMenuClose();
  };

  const username = constants.client_side_storage.userDetails.username

  React.useEffect(() => {
    (async () => {
      const res = await listChatSessions();
      const stored = res?.data ?? [];
      setSessions(stored);
      setActiveSessionId(stored.length > 0 ? stored[0].id : null);
    })();
  }, []);


  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={!isMobile && open}
        sx={{
          backgroundImage: `url(${headerBgColor})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundColor: "transparent",
          height: "65px",
        }}
      >
        <Toolbar>
          {/* Hamburger Menu - Only show on mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ mr: 2, color: "#0B2663" }}
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
            
          )}

          <Grid
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <img
              src={AbsyzLogo}
              alt="absyz-logo"
              style={{ height: "80px", width: "100px" }}
            />

            <Grid
              sx={{
                border: "1px solid #0088FF",
                borderRadius: "25px",
                padding: "0px 20px 0px 20px",
              }}
              className="application-container"
            >
              <p style={{ color: "#0B2663", fontSize: "0.85rem" }} className="application-name">
                {" "}
                Pre-Sales AI ChatBot
              </p>
            </Grid>

            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: "5px",
                alignItems: "center",
                width: "80px",
              }}
              className="username-container"
            >
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "5px" }}>
                <Tooltip
                  title={`Hi, ${username}`}
                  open={showTooltip}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                >
                  <img
                    src={UserIcon}
                    alt="user-icon"
                    style={{ height: "30px", width: "30px", cursor: "pointer" }}
                    onClick={handleIconClick}
                  />
                </Tooltip>

              </div>

              <img
                src={LogoutIcon}
                onClick={handleOpenLogoutModal}
                alt="logout-icon"
                style={{ height: "30px", width: "30px", cursor: "pointer" }}
              />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {isMobile ? (
        <MobileDrawer
          anchor="left"
          variant="temporary"
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <DrawerHeader />

          {/* New Chat Button Mobile */}
          <Box sx={{ p: 1,backgroundColor: "#087BDF" }} >
            <Box
              onClick={handleNewChat}
              sx={{
                border: "1px solid #0088FF",
                borderRadius: "8px",
                // padding: "10px",
                margin:"5px",
                cursor: "pointer",
                textAlign: "center",
                fontWeight: 500,
                color: "white",
                backgroundColor: "#4297eb",
                "&:hover": { backgroundColor: "#3094ec" },
              }}
            >
              <Button startIcon={<AddIcon />} sx={{ color: "white", textTransform: "none" }}>New Chat</Button>
            </Box>
          </Box>

          {/* Chat Sessions */}
          <Box sx={{ overflowY: "scroll",maxHeight:"350px", flexGrow: 1 ,display:"flex",flexDirection:"column",marginTop:"10px"  }}>
            {sessions.length === 0 ? (
              <Box sx={{ 
                padding: "20px", 
                textAlign: "center", 
                color: "#666",
                fontSize: "14px"
              }}>
                No chat sessions found
              </Box>
            ) : (
              <Virtuoso
                style={{ height: '100%' }}
                data={sessions}
                itemContent={(index, session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    isActive={activeSessionId === session.id}
                    isEditing={editingSessionId === session.id}
                    editingTitle={editingTitle}
                    onSessionClick={() => {
                      setActiveSessionId(session.id);
                      if (isMobile) setOpen(false);
                    }}
                    onTitleClick={handleTitleClick}
                    onTitleChange={setEditingTitle}
                    onSaveTitle={handleSaveTitle}
                    onCancelEdit={handleCancelEdit}
                    onTitleBlur={handleTitleBlur}
                    onMenuOpen={handleMenuOpen}
                  />
                )}
              />
            )}
          </Box>
        </MobileDrawer>
      ) : (
        <Drawer variant="permanent" open={!isMobile || open}>
          <DrawerHeader />

          {/* New Chat Button */}
          <Box sx={{ p: 1,backgroundColor: "#087BDF" }} >
            <Box
              onClick={handleNewChat}
              sx={{
                border: "1px solid #0088FF",
                borderRadius: "8px",
                // padding: "10px",
                margin:"5px",
                cursor: "pointer",
                textAlign: "center",
                fontWeight: 500,
                color: "white",
                backgroundColor: "#4297eb",
                "&:hover": { backgroundColor: "#3094ec" },
              }}
            >
              <Button startIcon={<AddIcon />} sx={{ color: "white", textTransform: "none" }}>New Chat</Button>
            </Box>
          </Box>

          {/* Chat Sessions */}
          <Box sx={{ overflowY: "auto",maxHeight:"500px", flexGrow: 1 ,display:"flex",flexDirection:"column",marginTop:"10px"  }}>
            {sessions.length === 0 ? (
              <Box sx={{ 
                padding: "20px", 
                textAlign: "center", 
                color: "#666",
                fontSize: "14px"
              }}>
                No chat sessions found
              </Box>
            ) : (
              <Virtuoso
                style={{ height: '100%' }}
                data={sessions}
                itemContent={(index, session) => (
                  <SessionItem
                    key={session.id}
                    session={session}
                    isActive={activeSessionId === session.id}
                    isEditing={editingSessionId === session.id}
                    editingTitle={editingTitle}
                    onSessionClick={() => {
                      setActiveSessionId(session.id);
                      if (isMobile) setOpen(false);
                    }}
                    onTitleClick={handleTitleClick}
                    onTitleChange={setEditingTitle}
                    onSaveTitle={handleSaveTitle}
                    onCancelEdit={handleCancelEdit}
                    onTitleBlur={handleTitleBlur}
                    onMenuOpen={handleMenuOpen}
                  />
                )}
              />
            )}
          </Box>
        </Drawer>
      )}

      {/* Session Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEditTitle}>
          Edit Title
        </MenuItem>
        <MenuItem
          onClick={() => selectedSessionForMenu && handleDeleteClick(selectedSessionForMenu)}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
         <MenuItem onClick={() => {
           const session = sessions.find(s => s.id === selectedSessionForMenu);
           if (session && selectedSessionForMenu) {
             handlePinToggle(selectedSessionForMenu, session.is_pinned);
           }
         }}>
          {sessions.find(s => s.id === selectedSessionForMenu)?.is_pinned ? 'Unpin' : 'Pin'}
        </MenuItem>
      </Menu>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          backgroundImage: `url(${BgColor})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
          paddingLeft: { xs: 0, sm: "0px" },
          width: { xs: "100vw", sm: "70%" },
          height: { xs: "100vh" }
        }}
      >
        <DrawerHeader />
        <Grid>
          <AIChatBox 
            open={open} 
            sessionId={activeSessionId} 
            onUpdateSessionTitle={updateSessionTitle} 
            isNewChatClicked={isNewChatClicked} 
            setIsNewChatClicked={setIsNewChatClicked}
            onCreateNewSession={handleCreateNewSession}
          />
        </Grid>
      </Box>
      {openLogoutModal && (
        <ReusableModal
          open={openLogoutModal}
          setOpen={setOpenLogoutModal}
          type="logout"
        />
      )}
      {deleteModalOpen && sessionToDelete && (
        <ReusableModal
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          type="delete-session"
          triggerCall={() => handleDeleteSession(sessionToDelete)}
        />
      )}
      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          colour={snackbarInfo.color}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          autoHideDuration={2000}
        />
      )}

    </Box>
  );
}
